/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * SEEP AI voice agent: configuration, the conversation log, and usage.
 *
 * Three tabs, in the order an admin needs them: set the assistant up, see
 * what it has been saying to customers, and see what it has used. The
 * minute balance is read-only here -- top-ups are sold by the platform team
 * (admin portal), not self-served, so the page says how to get more instead
 * of offering a button that cannot work.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
} from "@mui/material";
import { ArrowBack, Close, Lock } from "@mui/icons-material";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import { getApiErrorMessage } from "utils/apiErrors";
import SeepLogo from "components/SeepLogo";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

const CARD_BORDER = "1px solid #E6E8F0";
const CARD_SX = { p: 3, border: CARD_BORDER, boxShadow: "none", borderRadius: "12px" };

const PROFILE_LABELS = {
  auto: "Automatic (recommended)",
  s2s_gemini: "Standard",
  pipeline_indic: "Indic & Regional",
  s2s_openai: "Premium",
};
const PROFILE_HINTS = {
  auto: "Premium on the website once Premium is allowed, otherwise Standard; Indic & Regional for calls in Indian languages",
  s2s_gemini: "Quickest replies, every language",
  pipeline_indic: "Made for Indian languages",
  s2s_openai: "Most expressive, draws more minutes",
};
const TIER_BADGES = { standard: "Fastest", indic: "Made for India", premium: "Premium" };
const x = (value) => `×${Number((Number(value) || 1).toFixed(2))}`;
const GENDER_LABELS = { female: "Female voices", male: "Male voices", neutral: "Neutral" };

const OUTCOME_LABELS = {
  browse: "Browsed",
  lead_created: "Callback lead",
  order_started: "Went to checkout",
  order_paid: "Booked",
  handoff: "Handed to a person",
  abandoned: "No conversation",
  failed: "Could not start",
  active: "In progress",
  created: "Not joined",
};

const TOOL_LABELS = {
  search_packages: "Package search",
  get_package: "Package details",
  search_knowledge: "Company knowledge",
  create_lead: "Callback lead",
  request_handoff: "Hand to a person",
  "seep.navigate": "Open a page for the visitor",
  "seep.focus": "Point at part of the page",
  "seep.show_options": "Show recommendations on screen",
  "seep.add_to_cart": "Add to cart",
  switch_language: "Language switch",
};

// The widget's own default when neither the site nor the tenant sets a colour.
const WIDGET_FALLBACK_ACCENT = "#247cd6";

function contrastText(hex) {
  const n = parseInt((hex || "").slice(1), 16);
  if (Number.isNaN(n)) return "#fff";
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  return 0.299 * r + 0.587 * g + 0.114 * b > 160 ? "#111827" : "#fff";
}

function WidgetAppearanceCard({ settings, setSettings }) {
  const custom = settings.widget_accent || "";
  const accent = custom || WIDGET_FALLBACK_ACCENT;
  const setAccent = (value) => setSettings((s) => ({ ...s, widget_accent: value }));
  return (
    <Card sx={CARD_SX}>
      <MDTypography variant="h6" mb={1}>Website widget</MDTypography>
      <MDTypography variant="body2" color="text" mb={2}>
        By default the voice widget takes your website template&apos;s accent colour, so it matches
        the site. Pick a colour here to use your own instead.
      </MDTypography>
      <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={2}>
        <MDBox component="label" display="flex" alignItems="center" gap={1} sx={{ cursor: "pointer" }}>
          <MDBox component="input" type="color" value={accent} aria-label="Widget colour"
            onChange={(e) => setAccent(e.target.value)}
            sx={{ width: 44, height: 32, p: 0, border: CARD_BORDER, borderRadius: "8px", background: "none" }} />
          <MDTypography variant="button">{custom ? custom.toUpperCase() : "Website colour"}</MDTypography>
        </MDBox>
        {custom && (
          <MDButton size="small" variant="text" color="info" onClick={() => setAccent("")}>
            Use website colour
          </MDButton>
        )}
        <MDBox ml={{ md: "auto" }} display="flex" alignItems="center" gap={1.25} px={1.5} py={1}
          aria-label="Preview of the widget during a conversation"
          sx={{ borderRadius: "999px", border: CARD_BORDER, background: "#fff", boxShadow: "0 6px 20px rgba(15,23,42,.08)" }}>
          <MDBox sx={{ width: 26, height: 26, borderRadius: "50%",
            background: `radial-gradient(circle at 35% 30%, #fff 0, ${accent} 55%)`,
            boxShadow: `0 0 0 4px ${accent}33` }} />
          <MDTypography variant="button" fontWeight="medium">Listening…</MDTypography>
          <MDBox component="span" sx={{ px: 1.25, py: 0.25, borderRadius: "999px", fontSize: 12,
            background: accent, color: contrastText(accent) }}>End</MDBox>
        </MDBox>
      </MDBox>
      {!custom && (
        <MDTypography variant="caption" color="text" display="block" mt={1}>
          The preview shows the widget&apos;s standard blue; on your site it uses the template colour.
        </MDTypography>
      )}
    </Card>
  );
}

const DAYS = [
  ["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"],
  ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"],
];

const minutes = (seconds) => Math.max(0, Math.floor((Number(seconds) || 0) / 60));
const duration = (seconds) => {
  const s = Number(seconds) || 0;
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
};
const when = (value) => (value ? new Date(value).toLocaleString() : "—");
const userLabel = (u) =>
  [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.username || `User ${u.id}`;

// ------------------------------------------------------------ business hours

function HoursEditor({ value, onChange }) {
  const hours = value || {};
  const always = !value;
  const setDay = (day, range) => {
    const next = { ...hours };
    if (range) next[day] = [range];
    else delete next[day];
    onChange(next);
  };
  return (
    <MDBox>
      <FormControlLabel
        control={<Switch checked={always} onChange={(e) => onChange(e.target.checked ? null : {
          mon: [["09:00", "18:00"]], tue: [["09:00", "18:00"]], wed: [["09:00", "18:00"]],
          thu: [["09:00", "18:00"]], fri: [["09:00", "18:00"]],
        })} />}
        label={<MDTypography variant="button">A person is always available to take transfers</MDTypography>}
      />
      {!always && (
        <Grid container spacing={1} mt={0.5}>
          {DAYS.map(([day, label]) => {
            const range = (hours[day] || [])[0];
            return (
              <Grid item xs={12} sm={6} md={4} key={day}>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Checkbox size="small" checked={Boolean(range)}
                    onChange={(e) => setDay(day, e.target.checked ? ["09:00", "18:00"] : null)} />
                  <MDTypography variant="button" sx={{ width: 36 }}>{label}</MDTypography>
                  <TextField type="time" size="small" disabled={!range} value={range?.[0] || "09:00"}
                    onChange={(e) => setDay(day, [e.target.value, range?.[1] || "18:00"])} />
                  <TextField type="time" size="small" disabled={!range} value={range?.[1] || "18:00"}
                    onChange={(e) => setDay(day, [range?.[0] || "09:00", e.target.value])} />
                </MDBox>
              </Grid>
            );
          })}
        </Grid>
      )}
      <MDTypography variant="caption" color="text">
        Outside these hours the assistant offers a callback instead of transferring a call.
      </MDTypography>
    </MDBox>
  );
}

// ------------------------------------------------------------------ settings

function tiersInUse(settings) {
  const used = { standard: [], indic: [], premium: [] };
  const mark = (choice, where) => {
    if (choice === "s2s_gemini") used.standard.push(where);
    else if (choice === "pipeline_indic") used.indic.push(where);
    else if (choice === "s2s_openai") used.premium.push(where);
    else if (where === "Phone") { used.standard.push(where); used.indic.push("Phone, Indian languages"); }
    else used.standard.push(where);
  };
  if (settings.web_enabled) mark(settings.voice_profile_web, "Website");
  if (settings.phone_enabled) mark(settings.voice_profile_phone, "Phone");
  return used;
}

function VoicesCard({ tiers, settings, setSettings, rates }) {
  const used = tiersInUse(settings);
  const choices = settings.voice_choices || {};
  const choose = (profile) => (e) =>
    setSettings((s) => ({ ...s, voice_choices: { ...(s.voice_choices || {}), [profile]: e.target.value } }));
  return (
    <Card sx={CARD_SX}>
      <MDTypography variant="h6">Voices</MDTypography>
      <MDTypography variant="body2" color="text" mb={2}>
        Seep has three voice tiers. Choose the voice customers hear in each; the tier used for a conversation
        is set above. Every voice speaks every language its tier supports.
      </MDTypography>
      <Grid container spacing={2}>
        {tiers.map((tier) => {
          const locked = tier.premium && !settings.premium_voice_enabled;
          const current = choices[tier.profile] || tier.default_voice;
          const groups = ["female", "male", "neutral"].map((g) => [g, tier.voices.filter((v) => v.gender === g)])
            .filter(([, list]) => list.length);
          const voice = tier.voices.find((v) => v.id === current);
          return (
            <Grid item xs={12} md={4} key={tier.profile}>
              <MDBox sx={{ border: CARD_BORDER, borderRadius: "12px", p: 2, height: "100%", opacity: locked ? 0.6 : 1,
                background: tier.premium ? "linear-gradient(160deg,#fff8e8,#fff 55%)" : "#fff" }}>
                <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                  <MDTypography variant="h6">{tier.label}</MDTypography>
                  <MDBox display="flex" gap={0.5}>
                    {tier.premium && rates && (
                      <Chip size="small" variant="outlined" color="warning" label={`${x(rates.premium)} minutes`} />
                    )}
                    <Chip size="small" label={TIER_BADGES[tier.tier] || tier.tier}
                      color={tier.premium ? "warning" : tier.tier === "indic" ? "success" : "info"} />
                  </MDBox>
                </MDBox>
                <MDTypography variant="body2" color="text" mb={1}>{tier.summary}</MDTypography>
                <MDTypography variant="caption" color="text" display="block" mb={tier.premium && rates ? 0.5 : 1.5}>
                  Best for: {tier.best_for}
                </MDTypography>
                {tier.premium && rates && (
                  <MDTypography variant="caption" color="warning" display="block" mb={1.5} fontWeight="medium">
                    Each minute uses {x(rates.premium)} the usual minutes: website {x(rates.web * rates.premium)},
                    phone {x(rates.phone * rates.premium)}.
                  </MDTypography>
                )}
                <TextField select fullWidth size="small" label="Voice" value={current} disabled={locked}
                  onChange={choose(tier.profile)}
                  SelectProps={{ renderValue: (id) => {
                    const v = tier.voices.find((x) => x.id === id);
                    return v ? `${v.label} · ${v.style}` : id;
                  } }}>
                  {groups.flatMap(([g, list]) => [
                    <MenuItem key={`h-${g}`} disabled dense sx={{ opacity: "1 !important", fontSize: 11, fontWeight: 600,
                      textTransform: "uppercase", letterSpacing: ".06em" }}>{GENDER_LABELS[g]}</MenuItem>,
                    ...list.map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                        {v.label}
                        <MDTypography component="span" variant="caption" color="text" ml={1}>{v.style}</MDTypography>
                        {v.id === tier.default_voice && (
                          <MDTypography component="span" variant="caption" color="info" ml={1}>default</MDTypography>
                        )}
                      </MenuItem>
                    )),
                  ])}
                </TextField>
                <MDBox display="flex" gap={0.5} flexWrap="wrap" mt={1.5} minHeight={24}>
                  {locked && <Chip size="small" icon={<Lock sx={{ fontSize: 14 }} />} label="Allow the Premium tier above" />}
                  {!locked && used[tier.tier].map((w) => <Chip key={w} size="small" variant="outlined" label={`In use: ${w}`} />)}
                  {!locked && used[tier.tier].length === 0 && (
                    <MDTypography variant="caption" color="text">Not in use with your current settings</MDTypography>
                  )}
                </MDBox>
                {voice && !locked && (
                  <MDTypography variant="caption" color="text" display="block" mt={1}>
                    {voice.label}: {voice.style.toLowerCase()} {voice.gender === "neutral" ? "voice" : `${voice.gender} voice`}.
                  </MDTypography>
                )}
              </MDBox>
            </Grid>
          );
        })}
        {tiers.length === 0 && (
          <Grid item xs={12}><MDTypography variant="caption" color="text">Voice options load once the backend is updated.</MDTypography></Grid>
        )}
      </Grid>
    </Card>
  );
}

function SettingsTab({ status, settings, setSettings, queues, users, onSave, saving }) {
  const set = (field) => (e) =>
    setSettings((s) => ({ ...s, [field]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));
  const languages = status.languages || [];
  const allowed = settings.allowed_languages || [];
  const toggleLanguage = (code) =>
    setSettings((s) => {
      const has = s.allowed_languages.includes(code);
      const next = has ? s.allowed_languages.filter((c) => c !== code) : [...s.allowed_languages, code];
      return { ...s, allowed_languages: next.length ? next : s.allowed_languages };
    });
  const profileOptions = (status.voice_profiles || ["auto"]).map((p) => (
    <MenuItem key={p} value={p} disabled={p === "s2s_openai" && !settings.premium_voice_enabled}>
      <MDBox>
        <MDTypography variant="button" display="block">{PROFILE_LABELS[p] || p}</MDTypography>
        {PROFILE_HINTS[p] && <MDTypography variant="caption" color="text">{PROFILE_HINTS[p]}</MDTypography>}
      </MDBox>
    </MenuItem>
  ));
  const renderProfile = (value) => PROFILE_LABELS[value] || value;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={6}>
        <Card sx={CARD_SX}>
          <MDTypography variant="h6" mb={1}>Where the assistant works</MDTypography>
          <FormControlLabel control={<Switch checked={settings.enabled} onChange={set("enabled")} />}
            label={<MDTypography variant="button" fontWeight="medium">Assistant switched on</MDTypography>} />
          <FormControlLabel control={<Switch checked={settings.web_enabled} onChange={set("web_enabled")} />}
            label={<MDTypography variant="button">On your website (voice widget)</MDTypography>} />
          <FormControlLabel
            control={<Switch checked={settings.phone_enabled} onChange={set("phone_enabled")}
              disabled={!status.phone_bridge_configured && !settings.phone_enabled} />}
            label={<MDTypography variant="button">On phone calls (phone menu option or number)</MDTypography>} />
          {!status.phone_bridge_configured && (
            <MDTypography variant="caption" color="text" display="block">
              Phone calls need the platform's phone bridge, which is not set up in this environment yet.
            </MDTypography>
          )}
          <MDTypography variant="caption" color="text" display="block" mt={1}>
            For calls, add a &ldquo;SEEP AI travel assistant&rdquo; key to a phone menu, or set a
            number to &ldquo;SEEP AI assistant, then a queue&rdquo; under Telephony &rarr; Phone numbers.
          </MDTypography>
          <Divider sx={{ my: 2 }} />
          <MDTypography variant="h6" mb={1}>Voice</MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth size="small" label="Website uses" value={settings.voice_profile_web}
                onChange={set("voice_profile_web")} SelectProps={{ renderValue: renderProfile }}>{profileOptions}</TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth size="small" label="Phone calls use" value={settings.voice_profile_phone}
                onChange={set("voice_profile_phone")} SelectProps={{ renderValue: renderProfile }}>{profileOptions}</TextField>
            </Grid>
          </Grid>
          <FormControlLabel sx={{ mt: 1 }}
            control={<Switch checked={settings.premium_voice_enabled} onChange={set("premium_voice_enabled")} />}
            label={<MDTypography variant="button">
              Allow the Premium tier{status.rates ? ` (uses ${x(status.rates.premium)} minutes)` : ""}
            </MDTypography>} />
          <MDTypography variant="caption" color="text" display="block">
            Pick the exact voice for each tier under Voices below.
          </MDTypography>
          <TextField fullWidth size="small" type="number" label="Longest conversation (minutes)" sx={{ mt: 2 }}
            value={Math.round((settings.max_session_seconds || 900) / 60)}
            inputProps={{ min: 1, max: 60 }}
            onChange={(e) => setSettings((s) => ({
              ...s, max_session_seconds: Math.min(60, Math.max(1, Number(e.target.value) || 1)) * 60,
            }))}
            helperText="The assistant wraps up politely a minute before this." />
        </Card>
      </Grid>

      <Grid item xs={12} lg={6}>
        <Card sx={CARD_SX}>
          <MDTypography variant="h6" mb={1}>Personality</MDTypography>
          <TextField fullWidth size="small" label="Name the assistant uses" value={settings.agent_display_name}
            onChange={set("agent_display_name")} margin="dense" inputProps={{ maxLength: 80 }} />
          <TextField fullWidth size="small" label="Greeting (optional)" value={settings.greeting || ""}
            onChange={set("greeting")} margin="dense" inputProps={{ maxLength: 500 }}
            helperText="Said in the customer's language. Leave empty for a short default greeting." />
          <TextField fullWidth multiline minRows={5} size="small" label="Guidance for the assistant (optional)"
            value={settings.persona_prompt || ""} onChange={set("persona_prompt")} margin="dense"
            inputProps={{ maxLength: 6000 }}
            helperText="Tone, what to highlight, what never to promise. It cannot override the built-in rules: the assistant never invents prices or takes card details." />
          <Divider sx={{ my: 2 }} />
          <MDTypography variant="h6" mb={1}>Languages</MDTypography>
          <MDTypography variant="body2" color="text" mb={1}>
            Customers never pick a language: Seep hears which one they speak and replies in it, switching
            whenever they do. Anything not ticked here, or not supported, gets a reply in English.
          </MDTypography>
          <MDBox display="flex" flexWrap="wrap" gap={1} mb={2}>
            {languages.map((l) => (
              <Chip key={l.code} label={l.label} clickable
                color={allowed.includes(l.code) ? "info" : "default"}
                variant={allowed.includes(l.code) ? "filled" : "outlined"}
                onClick={() => toggleLanguage(l.code)} />
            ))}
          </MDBox>
          <TextField select fullWidth size="small" label="Default language" value={settings.default_language}
            onChange={set("default_language")}
            helperText="Only the first greeting, when the visitor's browser gives no hint. If it is an English, it also sets the English Seep falls back to (Indian, Australian or American).">
            {languages.filter((l) => allowed.includes(l.code)).map((l) => (
              <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>
            ))}
          </TextField>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <WidgetAppearanceCard settings={settings} setSettings={setSettings} />
      </Grid>

      <Grid item xs={12}>
        <VoicesCard tiers={status.voice_tiers || []} settings={settings} setSettings={setSettings} rates={status.rates} />
      </Grid>

      <Grid item xs={12}>
        <Card sx={CARD_SX}>
          <MDTypography variant="h6" mb={1}>Handing over to your team</MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth size="small" label="Phone transfers go to"
                value={settings.handoff_queue_id || ""} onChange={set("handoff_queue_id")}
                helperText="Empty uses the number's queue, then the telephony default queue.">
                <MenuItem value="">Default</MenuItem>
                {queues.map((q) => <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth size="small" label="Callback leads are assigned to"
                value={settings.lead_owner_id || ""} onChange={set("lead_owner_id")}
                helperText="Your automations run on these leads like any other.">
                <MenuItem value="">First administrator</MenuItem>
                {users.map((u) => <MenuItem key={u.id} value={u.id}>{userLabel(u)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <HoursEditor value={settings.business_hours}
                onChange={(v) => setSettings((s) => ({ ...s, business_hours: v }))} />
            </Grid>
          </Grid>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <MDBox display="flex" justifyContent="flex-end">
          <MDButton variant="gradient" color="info" onClick={onSave} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </MDButton>
        </MDBox>
      </Grid>
    </Grid>
  );
}

// ------------------------------------------------------------- conversations

function SessionDialog({ sessionId, onClose }) {
  const { axiosInstance } = useAuth();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!sessionId) return;
    setDetail(null);
    setError("");
    axiosInstance.get(`/seep/sessions/${sessionId}`)
      .then((res) => setDetail(res.data))
      .catch((err) => setError(getApiErrorMessage(err, "Could not load the conversation.")));
  }, [axiosInstance, sessionId]);

  return (
    <Dialog open={Boolean(sessionId)} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        <MDBox flexGrow={1}>Conversation</MDBox>
        <IconButton onClick={onClose} aria-label="Close"><Close /></IconButton>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {!detail && !error && <MDBox py={4} textAlign="center"><CircularProgress size={28} /></MDBox>}
        {detail && (
          <>
            <MDBox display="flex" gap={1} flexWrap="wrap" mb={2}>
              <Chip size="small" label={detail.channel === "phone" ? "Phone" : "Website"} />
              <Chip size="small" label={detail.language || "—"} />
              <Chip size="small" label={OUTCOME_LABELS[detail.outcome] || detail.outcome || detail.status} color="info" />
              {detail.duration_seconds != null && <Chip size="small" label={duration(detail.duration_seconds)} />}
              {detail.lead_id && <Chip size="small" label={`Lead #${detail.lead_id}`} component="a" clickable href={`/leads/${detail.lead_id}`} />}
            </MDBox>
            {detail.summary && (
              <MDBox mb={2} p={1.5} sx={{ background: "#F6F8FB", borderRadius: "8px", whiteSpace: "pre-line" }}>
                <MDTypography variant="button">{detail.summary}</MDTypography>
              </MDBox>
            )}
            <MDTypography variant="h6" mb={1}>Transcript</MDTypography>
            {(detail.transcript || []).length === 0 && (
              <MDTypography variant="button" color="text">No transcript was recorded.</MDTypography>
            )}
            {(detail.transcript || []).map((line, i) => (
              <MDBox key={i} mb={1} display="flex" gap={1}>
                <MDTypography variant="caption" fontWeight="bold" sx={{ width: 76, flexShrink: 0 }}>
                  {line.role === "customer" ? "Customer" : "Assistant"}
                </MDTypography>
                <MDTypography variant="button">{line.text}</MDTypography>
              </MDBox>
            ))}
            {(detail.tool_calls || []).length > 0 && (
              <>
                <MDTypography variant="h6" mt={2} mb={1}>What the assistant looked up and did</MDTypography>
                <Table size="small">
                  <TableBody>
                    {detail.tool_calls.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell>{c.tool}</TableCell>
                        <TableCell>{c.status}{c.error ? `: ${c.error}` : ""}</TableCell>
                        <TableCell align="right">{c.latency_ms != null ? `${c.latency_ms} ms` : ""}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ConversationsTab() {
  const { axiosInstance } = useAuth();
  const [rows, setRows] = useState(null);
  const [channel, setChannel] = useState("");
  const [open, setOpen] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setRows(null);
    axiosInstance.get("/seep/sessions", { params: { limit: 100, ...(channel ? { channel } : {}) } })
      .then((res) => setRows(res.data || []))
      .catch((err) => { setRows([]); setError(getApiErrorMessage(err, "Could not load conversations.")); });
  }, [axiosInstance, channel]);

  return (
    <Card sx={CARD_SX}>
      <MDBox display="flex" alignItems="center" mb={2}>
        <MDTypography variant="h6" flexGrow={1}>Recent conversations</MDTypography>
        <TextField select size="small" value={channel} onChange={(e) => setChannel(e.target.value)} sx={{ width: 160 }}>
          <MenuItem value="">All channels</MenuItem>
          <MenuItem value="web">Website</MenuItem>
          <MenuItem value="phone">Phone</MenuItem>
        </TextField>
      </MDBox>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {rows === null ? <MDBox py={4} textAlign="center"><CircularProgress size={28} /></MDBox> : (
        <MDBox sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow>
                <TableCell>Started</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Language</TableCell>
                <TableCell>Length</TableCell>
                <TableCell>Outcome</TableCell>
                <TableCell>From</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={6}>No conversations yet.</TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} hover sx={{ cursor: "pointer" }} onClick={() => setOpen(r.id)}>
                  <TableCell>{when(r.agent_joined_at || r.created_at)}</TableCell>
                  <TableCell>{r.channel === "phone" ? "Phone" : "Website"}</TableCell>
                  <TableCell>{r.language || "—"}</TableCell>
                  <TableCell>{r.duration_seconds != null ? duration(r.duration_seconds) : r.status}</TableCell>
                  <TableCell>{OUTCOME_LABELS[r.outcome] || r.outcome || "—"}</TableCell>
                  <TableCell sx={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.channel === "phone" ? r.caller_number || "—" : r.page_url || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </MDBox>
      )}
      <SessionDialog sessionId={open} onClose={() => setOpen(null)} />
    </Card>
  );
}

// --------------------------------------------------------------------- usage

const CHART_COLORS = ["#1A73E8", "#27AA6B", "#F09A18", "#8E44AD", "#E5484D", "#0EA5A4", "#64748B", "#D946EF"];
const OUTCOME_COLORS = {
  lead_created: "#27AA6B", order_started: "#F09A18", order_paid: "#0EA5A4", handoff: "#8E44AD",
  browse: "#1A73E8", abandoned: "#CBD5E1", failed: "#E5484D", active: "#94A3B8", created: "#E2E8F0",
};
const RANGES = [[7, "7 days"], [30, "30 days"], [90, "90 days"]];

function Kpi({ label, value, hint, accent = "#1A73E8" }) {
  return (
    <Card sx={{ ...CARD_SX, p: 2.25, height: "100%", borderTop: `3px solid ${accent}` }}>
      <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ textTransform: "uppercase", letterSpacing: ".05em" }}>
        {label}
      </MDTypography>
      <MDTypography variant="h4" fontWeight="bold" lineHeight={1.25}>{value}</MDTypography>
      {hint && <MDTypography variant="caption" color="text">{hint}</MDTypography>}
    </Card>
  );
}

function Stat({ label, value, hint }) {
  return (
    <Card sx={{ ...CARD_SX, p: 2.5, height: "100%" }}>
      <MDTypography variant="caption" color="text">{label}</MDTypography>
      <MDTypography variant="h4" fontWeight="medium">{value}</MDTypography>
      {hint && <MDTypography variant="caption" color="text">{hint}</MDTypography>}
    </Card>
  );
}

/**
 * Charts draw without an entry animation. The cards settle their width just
 * after mounting (the grid and the low-balance banner lay out), and Recharts
 * keeps animated bars and lines at the size they started animating from, so
 * on QA the trend drew squeezed against axes of the final width.
 */
function ChartCard({ title, subtitle, children, height = 260 }) {
  return (
    <Card sx={{ ...CARD_SX, height: "100%" }}>
      <MDTypography variant="h6">{title}</MDTypography>
      {subtitle && <MDTypography variant="caption" color="text">{subtitle}</MDTypography>}
      <MDBox mt={1.5} height={height}>{children}</MDBox>
    </Card>
  );
}

function Empty({ text = "No conversations in this period yet." }) {
  return (
    <MDBox height="100%" display="flex" alignItems="center" justifyContent="center">
      <MDTypography variant="caption" color="text">{text}</MDTypography>
    </MDBox>
  );
}

function Donut({ rows, labelOf }) {
  const data = rows.filter((r) => Number(r.sessions) > 0).map((r) => ({ name: labelOf(r), value: Number(r.sessions) }));
  if (!data.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%" debounce={80}>
      <PieChart>
        <Pie isAnimationActive={false} data={data} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="85%" paddingAngle={2}>
          {data.map((d, i) => <Cell key={d.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Pie>
        <RTooltip formatter={(v, n) => [`${v} conversations`, n]} />
        <Legend verticalAlign="bottom" height={40} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

/** Fill the gaps so the trend shows quiet days as zero, not as a jump. */
function fillDays(daily, days) {
  const byDay = Object.fromEntries((daily || []).map((d) => [d.day, d]));
  const out = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    const key = d.toISOString().slice(0, 10);
    const row = byDay[key] || { conversations: 0, leads: 0, checkouts: 0, minutes: 0 };
    out.push({ ...row, day: key, label: d.toLocaleDateString(undefined, { day: "numeric", month: "short" }) });
  }
  return out;
}

/** Hours arrive in UTC; show them in the viewer's own time zone. */
function localHours(hoursUtc) {
  const offset = -new Date().getTimezoneOffset() / 60;
  const counts = Array(24).fill(0);
  (hoursUtc || []).forEach((h) => {
    const local = (((Math.round(h.hour + offset)) % 24) + 24) % 24;
    counts[local] += Number(h.sessions) || 0;
  });
  return counts.map((sessions, hour) => ({
    hour, sessions, label: new Date(2000, 0, 1, hour).toLocaleTimeString(undefined, { hour: "numeric" }),
  }));
}

function UsageTab({ status }) {
  const { axiosInstance } = useAuth();
  const [days, setDays] = useState(30);
  const [wallet, setWallet] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axiosInstance.get("/seep/wallet").then((w) => setWallet(w.data))
      .catch((err) => setError(getApiErrorMessage(err, "Could not load your minutes.")));
  }, [axiosInstance]);
  useEffect(() => {
    setReport(null);
    axiosInstance.get("/seep/analytics", { params: { days } }).then((r) => setReport(r.data))
      .catch((err) => setError(getApiErrorMessage(err, "Could not load analytics.")));
  }, [axiosInstance, days]);

  const languageLabel = useMemo(() => {
    const map = Object.fromEntries((status?.languages || []).map((l) => [l.code, l.label]));
    return (r) => map[r.key] || r.key || "Not detected";
  }, [status]);
  const trend = useMemo(() => fillDays(report?.daily, days), [report, days]);
  const hours = useMemo(() => localHours(report?.hours_utc), [report]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!wallet) return <MDBox py={4} textAlign="center"><CircularProgress size={28} /></MDBox>;

  const t = report?.totals;
  const pct = (v) => (v == null ? "—" : `${v}%`);
  const busiest = hours.reduce((best, h) => (h.sessions > best.sessions ? h : best), { sessions: 0 });
  const outcomes = (report?.outcomes || []).map((o) => ({
    name: OUTCOME_LABELS[o.outcome] || o.outcome, value: Number(o.sessions), color: OUTCOME_COLORS[o.outcome] || "#94A3B8",
  }));

  return (
    <Grid container spacing={3}>
      {wallet.low_balance && (
        <Grid item xs={12}>
          <Alert severity="warning">
            Your minute balance is low. Contact your PearlCRM account manager to add minutes; when it runs
            out the assistant stops taking new conversations and callers go straight to your team.
          </Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <MDBox>
            <MDTypography variant="h5">How Seep is doing</MDTypography>
            <MDTypography variant="caption" color="text">Conversations, results and minutes, updated live.</MDTypography>
          </MDBox>
          <MDBox display="flex" gap={1}>
            {RANGES.map(([value, label]) => (
              <MDButton key={value} size="small" variant={days === value ? "gradient" : "outlined"} color="info"
                onClick={() => setDays(value)}>{label}</MDButton>
            ))}
          </MDBox>
        </MDBox>
      </Grid>

      {!report ? (
        <Grid item xs={12}><MDBox py={4} textAlign="center"><CircularProgress size={28} /></MDBox></Grid>
      ) : (
        <>
          <Grid item xs={6} md={2}><Kpi label="Conversations" value={t.conversations}
            hint={`${t.sessions - t.conversations} opened without talking`} /></Grid>
          <Grid item xs={6} md={2}><Kpi label="Callback leads" value={t.leads} hint={`${pct(t.lead_rate)} of conversations`} accent="#27AA6B" /></Grid>
          <Grid item xs={6} md={2}><Kpi label="Sent to checkout" value={t.checkouts}
            hint={`${pct(t.checkout_rate)} · ${t.bookings} booked`} accent="#F09A18" /></Grid>
          <Grid item xs={6} md={2}><Kpi label="Handed to a person" value={t.handoffs} hint={pct(t.handoff_rate)} accent="#8E44AD" /></Grid>
          <Grid item xs={6} md={2}><Kpi label="Minutes talked" value={minutes(t.billable_seconds)}
            hint={`${minutes(t.credits)} from your balance · avg ${duration(Math.round(t.avg_duration_seconds || 0))}`} accent="#0EA5A4" /></Grid>
          <Grid item xs={6} md={2}><Kpi label="Reply time" value={report.latency.p50_seconds != null ? `${report.latency.p50_seconds}s` : "—"}
            hint={report.latency.p95_seconds != null ? `Slowest 5%: ${report.latency.p95_seconds}s` : "Measured on new conversations"} accent="#64748B" /></Grid>

          <Grid item xs={12} lg={8}>
            <ChartCard title="Conversations and results" subtitle={`Per day, last ${days} days`} height={280}>
              {t.sessions === 0 ? <Empty /> : (
                <ResponsiveContainer width="100%" height="100%" debounce={80}>
                  <ComposedChart data={trend} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                    {/* band, not the default point scale: with points Recharts shifts bars off their days */}
                    <XAxis dataKey="label" scale="band" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={16} />
                    <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                    <RTooltip />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                    <Bar isAnimationActive={false} yAxisId="left" dataKey="conversations" name="Conversations" fill="#1A73E8" radius={[4, 4, 0, 0]} maxBarSize={28} />
                    <Line isAnimationActive={false} yAxisId="left" type="monotone" dataKey="leads" name="Leads" stroke="#27AA6B" strokeWidth={2} dot={false} />
                    <Line isAnimationActive={false} yAxisId="left" type="monotone" dataKey="checkouts" name="Checkouts" stroke="#F09A18" strokeWidth={2} dot={false} />
                    <Area isAnimationActive={false} yAxisId="right" type="monotone" dataKey="minutes" name="Minutes" fill="#0EA5A433" stroke="#0EA5A4" />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>
          <Grid item xs={12} lg={4}>
            <ChartCard title="How conversations ended" height={280}>
              {!outcomes.length ? <Empty /> : (
                <ResponsiveContainer width="100%" height="100%" debounce={80}>
                  <BarChart data={outcomes} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                    <RTooltip formatter={(v) => [`${v} conversations`, ""]} />
                    <Bar isAnimationActive={false} dataKey="value" radius={[0, 4, 4, 0]}>
                      {outcomes.map((o) => <Cell key={o.name} fill={o.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <ChartCard title="Languages spoken"><Donut rows={report.languages} labelOf={languageLabel} /></ChartCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <ChartCard title="Website and phone">
              <Donut rows={report.channels} labelOf={(r) => (r.key === "phone" ? "Phone" : "Website")} />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <ChartCard title="Voice tiers used"><Donut rows={report.voice_tiers} labelOf={(r) => r.label} /></ChartCard>
          </Grid>

          <Grid item xs={12} lg={7}>
            <ChartCard title="Busiest hours" subtitle={busiest.sessions ? `Most conversations start around ${busiest.label}, your time` : "Your local time"} height={220}>
              {t.conversations === 0 ? <Empty /> : (
                <ResponsiveContainer width="100%" height="100%" debounce={80}>
                  <BarChart data={hours} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={2} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <RTooltip formatter={(v) => [`${v} conversations`, ""]} />
                    <Bar isAnimationActive={false} dataKey="sessions" fill="#1A73E8" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>
          <Grid item xs={12} lg={5}>
            <Card sx={{ ...CARD_SX, height: "100%" }}>
              <MDTypography variant="h6">Packages customers asked about</MDTypography>
              <MDTypography variant="caption" color="text">Opened or discussed in detail by Seep</MDTypography>
              <MDBox mt={1.5}>
                {report.top_packages.length === 0 && <MDTypography variant="caption" color="text">None yet.</MDTypography>}
                {report.top_packages.map((p) => {
                  const max = report.top_packages[0].sessions || 1;
                  return (
                    <MDBox key={p.slug} mb={1.25}>
                      <MDBox display="flex" justifyContent="space-between">
                        <MDTypography variant="button" fontWeight="medium" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: 1 }}>{p.title}</MDTypography>
                        <MDTypography variant="caption" color="text">{p.sessions}</MDTypography>
                      </MDBox>
                      <MDBox height={6} borderRadius="3px" bgColor="#EEF2F7">
                        <MDBox height={6} borderRadius="3px" sx={{ width: `${(100 * p.sessions) / max}%`, background: "#1A73E8" }} />
                      </MDBox>
                    </MDBox>
                  );
                })}
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={CARD_SX}>
              <MDTypography variant="h6">Assistant health</MDTypography>
              <MDTypography variant="caption" color="text">
                Reply time is from the moment the customer stops speaking to Seep&apos;s first word
                ({report.latency.measured_sessions} conversations measured). Lookups are the tools Seep uses mid-conversation.
              </MDTypography>
              <MDBox sx={{ overflowX: "auto" }} mt={1}>
                <Table size="small">
                  <TableHead sx={{ display: "table-header-group" }}>
                    <TableRow><TableCell>Lookup</TableCell><TableCell>Calls</TableCell><TableCell>Failed</TableCell><TableCell>Typical time</TableCell></TableRow>
                  </TableHead>
                  <TableBody>
                    {report.tools.length === 0 && <TableRow><TableCell colSpan={4}>No lookups yet.</TableCell></TableRow>}
                    {report.tools.map((r) => (
                      <TableRow key={r.tool}>
                        <TableCell>{TOOL_LABELS[r.tool] || r.tool}</TableCell>
                        <TableCell>{r.calls}</TableCell>
                        <TableCell>
                          {r.errors ? <Chip size="small" color="error" label={`${r.errors} (${Math.round((100 * r.errors) / r.calls)}%)`} /> : "0"}
                        </TableCell>
                        <TableCell>{r.p50_ms != null ? `${r.p50_ms} ms` : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </MDBox>
            </Card>
          </Grid>
        </>
      )}

      <Grid item xs={12}><MDTypography variant="h6">Minutes</MDTypography></Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Stat label="Minutes left" value={minutes(wallet.balance_seconds)}
          hint={`Monthly allowance ${minutes(wallet.included_seconds_monthly)} min`} />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Stat label="Allowance left this month" value={minutes(wallet.included_balance_seconds)}
          hint={wallet.cycle_resets_at ? `Resets ${new Date(wallet.cycle_resets_at).toLocaleDateString()}` : "No monthly allowance"} />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Stat label="Purchased minutes" value={minutes(wallet.topup_balance_seconds)} hint="Never expire" />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ ...CARD_SX, p: 2.5, height: "100%" }}>
          <MDTypography variant="caption" color="text">Minute rates</MDTypography>
          {[
            ["Website", wallet.web_credit_multiplier],
            ["Phone", wallet.phone_credit_multiplier],
            ["Premium voice", wallet.premium_credit_multiplier],
          ].map(([label, value]) => (
            <MDBox key={label} display="flex" justifyContent="space-between">
              <MDTypography variant="button">{label}</MDTypography>
              <MDTypography variant="button" fontWeight="bold">{x(value)}</MDTypography>
            </MDBox>
          ))}
          <MDTypography variant="caption" color="text" display="block" mt={0.5}>
            Premium stacks on the channel: a Premium phone minute uses
            {" "}{x((wallet.phone_credit_multiplier || 1) * (wallet.premium_credit_multiplier || 1))}.
          </MDTypography>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Card sx={CARD_SX}>
          <MDTypography variant="h6" mb={1}>Minute history</MDTypography>
          <Table size="small">
            <TableBody>
              {(wallet.transactions || []).slice(0, 20).map((tx, i) => (
                <TableRow key={i}>
                  <TableCell>{when(tx.at)}</TableCell>
                  <TableCell>{{ grant: "Monthly allowance", expire: "Allowance expired", topup: "Minutes added", debit: "Conversation", adjust: "Adjustment" }[tx.kind] || tx.kind}</TableCell>
                  <TableCell align="right">{tx.seconds > 0 ? "+" : ""}{Math.round(tx.seconds / 60 * 10) / 10} min</TableCell>
                </TableRow>
              ))}
              {(wallet.transactions || []).length === 0 && <TableRow><TableCell>No minutes yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      </Grid>
    </Grid>
  );
}

// ----------------------------------------------------------------- knowledge

const KIND_LABELS = {
  company: "About the company",
  policy: "Policy",
  offer: "Offer",
  faq: "FAQ",
  other: "Other",
};
const EMPTY_ENTRY = { kind: "faq", title: "", body: "", is_active: true, pinned: false, valid_from: "", valid_until: "" };

function KnowledgeDialog({ entry, onClose, onSaved }) {
  const { axiosInstance } = useAuth();
  const [form, setForm] = useState(entry);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => setForm(entry), [entry]);
  if (!form) return null;
  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const save = async () => {
    setSaving(true);
    setError("");
    const payload = {
      kind: form.kind,
      title: form.title.trim(),
      body: form.body.trim(),
      is_active: form.is_active,
      pinned: form.pinned,
      valid_from: form.valid_from || null,
      valid_until: form.valid_until || null,
    };
    try {
      if (form.id) await axiosInstance.put(`/seep/knowledge/${form.id}`, payload);
      else await axiosInstance.post("/seep/knowledge", payload);
      onSaved();
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not save this entry."));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{form.id ? "Edit knowledge" : "Add knowledge"}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField select fullWidth size="small" label="Type" value={form.kind} onChange={set("kind")} margin="dense">
          {Object.entries(KIND_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
        </TextField>
        <TextField fullWidth size="small" label={form.kind === "faq" ? "Question" : "Title"} value={form.title}
          onChange={set("title")} margin="dense" inputProps={{ maxLength: 200 }} />
        <TextField fullWidth multiline minRows={5} size="small" label={form.kind === "faq" ? "Answer" : "Details"}
          value={form.body} onChange={set("body")} margin="dense" inputProps={{ maxLength: 8000 }}
          helperText="Write it in English, the way you would explain it to a new colleague. Seep translates when it speaks." />
        <Grid container spacing={2} mt={0}>
          <Grid item xs={6}>
            <TextField fullWidth size="small" type="date" label="Valid from" value={form.valid_from || ""}
              onChange={set("valid_from")} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" type="date" label="Valid until" value={form.valid_until || ""}
              onChange={set("valid_until")} InputLabelProps={{ shrink: true }}
              helperText="Mostly for offers. Seep stops mentioning it after this date." />
          </Grid>
        </Grid>
        <FormControlLabel control={<Switch checked={form.pinned} onChange={set("pinned")} />}
          label={<MDTypography variant="button">Always know this (added to every conversation)</MDTypography>} />
        <MDTypography variant="caption" color="text" display="block" mb={1}>
          Keep pinned entries short: your company story, opening hours, the one policy everyone asks about.
          Everything else Seep looks up only when a customer asks. Offers are always included while they are valid.
        </MDTypography>
        <FormControlLabel control={<Switch checked={form.is_active} onChange={set("is_active")} />}
          label={<MDTypography variant="button">Active</MDTypography>} />
        <MDBox display="flex" justifyContent="flex-end" gap={1} mt={2}>
          <MDButton variant="outlined" color="secondary" onClick={onClose}>Cancel</MDButton>
          <MDButton variant="gradient" color="info" onClick={save}
            disabled={saving || !form.title.trim() || !form.body.trim()}>
            {saving ? "Saving…" : "Save"}
          </MDButton>
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}

function KnowledgeTab({ settings, setSettings, onSave, saving }) {
  const { axiosInstance } = useAuth();
  const [entries, setEntries] = useState(null);
  const [overview, setOverview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [question, setQuestion] = useState("");
  const [answers, setAnswers] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setError("");
    Promise.all([axiosInstance.get("/seep/knowledge"), axiosInstance.get("/seep/knowledge/overview")])
      .then(([list, ov]) => { setEntries(list.data); setOverview(ov.data); })
      .catch((err) => setError(getApiErrorMessage(err, "Could not load what Seep knows.")));
  }, [axiosInstance]);
  useEffect(() => { load(); }, [load]);

  const remove = async (entry) => {
    try {
      await axiosInstance.delete(`/seep/knowledge/${entry.id}`);
      load();
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not delete this entry."));
    }
  };
  const ask = async () => {
    if (!question.trim()) return;
    try {
      const res = await axiosInstance.post("/seep/knowledge/test", { query: question.trim(), limit: 4 });
      setAnswers(res.data.results || []);
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not run the test."));
    }
  };

  if (!entries || !overview) {
    return error ? <Alert severity="error">{error}</Alert> : <MDBox py={6} textAlign="center"><CircularProgress /></MDBox>;
  }
  const pkg = overview.packages || {};
  return (
    <Grid container spacing={3}>
      {error && <Grid item xs={12}><Alert severity="error">{error}</Alert></Grid>}
      <Grid item xs={12} lg={5}>
        <Card sx={CARD_SX}>
          <MDTypography variant="h6" mb={1}>What Seep knows</MDTypography>
          <MDTypography variant="body2" color="text" mb={2}>
            Seep reads this live from your CRM and website, so a package or FAQ you publish is known straight away.
          </MDTypography>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={6}><Stat label="Packages on the website" value={pkg.package_count ?? 0}
              hint={(pkg.destinations || []).slice(0, 4).join(", ")} /></Grid>
            <Grid item xs={6}><Stat label="Published FAQs" value={overview.faqs} hint="From your trips and packages" /></Grid>
            <Grid item xs={6}><Stat label="Website pages" value={(overview.website_pages || []).length}
              hint="Published builder pages" /></Grid>
            <Grid item xs={6}><Stat label="Your knowledge" value={overview.knowledge_entries} hint="Added on this page" /></Grid>
          </Grid>
          <MDTypography variant="button" fontWeight="medium">Every conversation starts with</MDTypography>
          <MDBox component="pre" sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 13, bgcolor: "#F7F9FC",
            border: CARD_BORDER, borderRadius: "8px", p: 1.5, mt: 1, mb: 2 }}>
            {overview.brief || "Nothing yet. Add a company entry and mark it \u201calways know this\u201d."}
          </MDBox>
          <FormControlLabel
            control={<Switch checked={Boolean(settings.share_coupon_offers)}
              onChange={(e) => setSettings((s) => ({ ...s, share_coupon_offers: e.target.checked }))} />}
            label={<MDTypography variant="button">Let Seep tell customers about public coupon codes</MDTypography>} />
          <MDTypography variant="caption" color="text" display="block" mb={1}>
            Only coupons that are live, have uses left and are not tied to one customer. Leave off if some codes are private.
          </MDTypography>
          <MDButton size="small" variant="outlined" color="info" onClick={onSave} disabled={saving}>
            {saving ? "Saving…" : "Save coupon setting"}
          </MDButton>
          <Divider sx={{ my: 2 }} />
          <MDTypography variant="button" fontWeight="medium">Try a customer question</MDTypography>
          <MDBox display="flex" gap={1} mt={1}>
            <TextField fullWidth size="small" placeholder="e.g. can I cancel and get a refund?" value={question}
              onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") ask(); }} />
            <MDButton variant="gradient" color="info" onClick={ask}>Ask</MDButton>
          </MDBox>
          {answers && (
            <MDBox mt={1}>
              {answers.length === 0 && (
                <MDTypography variant="caption" color="text">
                  Nothing found. Seep would offer a callback. Add an entry below to answer it.
                </MDTypography>
              )}
              {answers.map((a, i) => (
                <MDBox key={i} py={1} borderBottom={CARD_BORDER}>
                  <MDTypography variant="caption" color="info" fontWeight="medium">
                    {{ knowledge: "Your knowledge", faq: "FAQ", website: "Website" }[a.source] || a.source}
                  </MDTypography>
                  <MDTypography variant="button" display="block" fontWeight="medium">{a.title}</MDTypography>
                  <MDTypography variant="caption" color="text">{a.text}</MDTypography>
                </MDBox>
              ))}
            </MDBox>
          )}
        </Card>
      </Grid>

      <Grid item xs={12} lg={7}>
        <Card sx={CARD_SX}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <MDTypography variant="h6">Your knowledge</MDTypography>
            <MDButton variant="gradient" color="info" size="small" onClick={() => setEditing({ ...EMPTY_ENTRY })}>
              Add knowledge
            </MDButton>
          </MDBox>
          <MDTypography variant="body2" color="text" mb={2}>
            Anything a customer might ask that is not on a package: who you are, payment and cancellation
            policies, visas help, current offers, office hours.
          </MDTypography>
          <Table size="small">
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow>
                <TableCell>Title</TableCell><TableCell>Type</TableCell><TableCell>Status</TableCell><TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length === 0 && (
                <TableRow><TableCell colSpan={4}>Nothing added yet. Start with a short &ldquo;About us&rdquo; and pin it.</TableCell></TableRow>
              )}
              {entries.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <MDTypography variant="button" fontWeight="medium" display="block">{e.title}</MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ display: "-webkit-box", WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical", overflow: "hidden" }}>{e.body}</MDTypography>
                  </TableCell>
                  <TableCell>{KIND_LABELS[e.kind] || e.kind}</TableCell>
                  <TableCell>
                    <MDBox display="flex" gap={0.5} flexWrap="wrap">
                      {!e.is_active && <Chip size="small" label="Off" />}
                      {e.pinned && <Chip size="small" color="info" label="Always known" />}
                      {e.valid_until && <Chip size="small" variant="outlined" label={`Until ${e.valid_until}`} />}
                    </MDBox>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <MDButton size="small" variant="text" color="info"
                      onClick={() => setEditing({ ...EMPTY_ENTRY, ...e, valid_from: e.valid_from || "", valid_until: e.valid_until || "" })}>
                      Edit
                    </MDButton>
                    <MDButton size="small" variant="text" color="error" onClick={() => remove(e)}>Delete</MDButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {(overview.website_pages || []).length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <MDTypography variant="button" fontWeight="medium">Read from your website</MDTypography>
              <MDBox display="flex" gap={1} flexWrap="wrap" mt={1}>
                {overview.website_pages.map((p) => (
                  <Chip key={p.path} size="small" variant="outlined" label={`${p.title} (${p.path})`} />
                ))}
              </MDBox>
            </>
          )}
        </Card>
      </Grid>
      {editing && (
        <KnowledgeDialog entry={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </Grid>
  );
}

// ---------------------------------------------------------------------- page

export default function SeepAISettingsPage() {
  const navigate = useNavigate();
  const isV2 = useTheme()?.pearlUi === "v2";
  const { axiosInstance } = useAuth();
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const statusRes = await axiosInstance.get("/seep/status");
      setStatus(statusRes.data);
      if (!statusRes.data.licensed) return;
      const settingsRes = await axiosInstance.get("/seep/settings");
      setSettings(settingsRes.data);
      const [queuesRes, usersRes] = await Promise.allSettled([
        axiosInstance.get("/call-center/queues", { params: { with_stats: false } }),
        axiosInstance.get("/users/"),
      ]);
      setQueues(queuesRes.status === "fulfilled" && Array.isArray(queuesRes.value.data) ? queuesRes.value.data : []);
      setUsers(usersRes.status === "fulfilled" && Array.isArray(usersRes.value.data)
        ? usersRes.value.data.filter((u) => u.is_active && !u.is_support_user) : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not load SEEP AI settings."));
    }
  }, [axiosInstance]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        enabled: settings.enabled,
        web_enabled: settings.web_enabled,
        phone_enabled: settings.phone_enabled,
        agent_display_name: (settings.agent_display_name || "").trim() || "Seep",
        persona_prompt: settings.persona_prompt || null,
        greeting: settings.greeting || null,
        default_language: settings.default_language,
        allowed_languages: settings.allowed_languages,
        voice_profile_web: settings.voice_profile_web,
        voice_profile_phone: settings.voice_profile_phone,
        premium_voice_enabled: settings.premium_voice_enabled,
        handoff_queue_id: settings.handoff_queue_id || null,
        lead_owner_id: settings.lead_owner_id || null,
        business_hours: settings.business_hours || null,
        max_session_seconds: settings.max_session_seconds,
        share_coupon_offers: Boolean(settings.share_coupon_offers),
        voice_choices: settings.voice_choices || {},
        widget_accent: settings.widget_accent || "",
      };
      const res = await axiosInstance.put("/seep/settings", payload);
      setSettings(res.data);
      setToast("SEEP AI settings saved.");
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not save the settings."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {isV2 ? (
          <PageHeader title="SEEP AI voice assistant" summary="Talks to website visitors and callers in their language, recommends your packages, helps them book, and hands over to your team when they need a person." />
        ) : (
        <MDBox display="flex" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={() => navigate("/settings")} aria-label="Back to settings"><ArrowBack /></IconButton>
          <SeepLogo size={52} />
          <MDBox flexGrow={1} ml={0.5}>
            <MDTypography variant="h5" fontWeight="medium">SEEP AI voice assistant</MDTypography>
            <MDTypography variant="body2" color="text">
              Talks to website visitors and callers in their language, recommends your packages, helps them
              book, and hands over to your team when they need a person.
            </MDTypography>
          </MDBox>
        </MDBox>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!status && !error && <MDBox py={6} textAlign="center"><CircularProgress /></MDBox>}

        {status && !status.licensed && (
          <Card sx={{ ...CARD_SX, textAlign: "center", py: 6 }}>
            <Lock sx={{ fontSize: 40, color: "#98A2B3", mx: "auto" }} />
            <MDTypography variant="h6" mt={1}>SEEP AI is an add-on</MDTypography>
            <MDTypography variant="body2" color="text">
              Ask your PearlCRM account manager to add it to your subscription. It is billed on the minutes
              it talks.
            </MDTypography>
          </Card>
        )}

        {status?.licensed && !status.platform_configured && (
          <Alert severity="info" sx={{ mb: 2 }}>
            SEEP AI is included in your subscription but is not switched on for this environment yet. You can
            prepare your settings now; the assistant will start working once the platform team enables it.
          </Alert>
        )}

        {status?.licensed && settings && (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, maxWidth: 680 }}>
              <Tab label="Settings" />
              <Tab label="Knowledge" />
              <Tab label="Conversations" />
              <Tab label="Analytics & minutes" />
            </Tabs>
            {tab === 0 && (
              <SettingsTab status={status} settings={settings} setSettings={setSettings}
                queues={queues} users={users} onSave={save} saving={saving} />
            )}
            {tab === 1 && (
              <KnowledgeTab settings={settings} setSettings={setSettings} onSave={save} saving={saving} />
            )}
            {tab === 2 && <ConversationsTab />}
            {tab === 3 && <UsageTab status={status} />}
          </>
        )}
      </MDBox>
      <Snackbar open={Boolean(toast)} autoHideDuration={3000} onClose={() => setToast("")} message={toast} />
    </DashboardLayout>
  );
}
